import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "@/context/AuthContext";

const modules = [
  { code: "D1", name: "Source" },
  { code: "D2", name: "Clean" },
  { code: "D3", name: "Identify" },
  { code: "D4", name: "Score" },
  { code: "D5", name: "Route" },
  { code: "D6", name: "Activate" },
];

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const [modOpen, setModOpen] = useState(false);
  const [shrunk, setShrunk] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setShrunk(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const portalHref =
    user && user.role
      ? user.role === "admin"
        ? "/admin"
        : user.role === "partner"
        ? "/partner"
        : user.role === "buyer"
        ? "/buyer"
        : "/client"
      : "/login";

  return (
    <header
      className="sticky top-0 z-40 border-b transition-all duration-300"
      style={{
        background: shrunk ? "rgba(11,14,20,0.92)" : "rgba(11,14,20,0.72)",
        backdropFilter: "blur(14px)",
        borderColor: shrunk ? "var(--dt6-border)" : "transparent",
        boxShadow: shrunk ? "0 10px 30px -20px rgba(0,0,0,0.6)" : "none",
      }}
      data-testid="public-nav"
    >
      <div
        className="max-w-7xl mx-auto flex items-center justify-between px-6 transition-all duration-300"
        style={{ paddingTop: shrunk ? "0.75rem" : "1rem", paddingBottom: shrunk ? "0.75rem" : "1rem" }}
      >
        <div className="flex items-center gap-8">
          <Logo size={26} showTagline />
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <NavLink to="/platform" className="text-[color:var(--dt6-text)] hover:text-[color:var(--dt6-primary)]" data-testid="nav-platform">Platform</NavLink>
            <NavLink to="/standard" className="text-[color:var(--dt6-text)] hover:text-[color:var(--dt6-primary)]" data-testid="nav-standard">The Standard</NavLink>
            <div
              className="relative"
              onMouseEnter={() => setModOpen(true)}
              onMouseLeave={() => setModOpen(false)}
            >
              <button
                type="button"
                className="text-[color:var(--dt6-text)] hover:text-[color:var(--dt6-primary)] inline-flex items-center gap-1"
                data-testid="nav-modules"
              >
                Modules <ChevronDown size={14} strokeWidth={1.5} />
              </button>
              {modOpen && (
                <div
                  className="absolute left-0 top-full pt-2 w-64"
                >
                  <div className="dt6-card p-2">
                  {modules.map((m) => (
                    <Link
                      key={m.code}
                      to={`/modules/${m.code}`}
                      className="flex items-center justify-between px-3 py-2 hover:bg-[color:var(--dt6-surface-2)]"
                      data-testid={`nav-module-${m.code}`}
                    >
                      <span className="text-sm">{m.name}</span>
                      <span className="font-mono text-[0.7rem] text-[color:var(--dt6-muted)]">{m.code}</span>
                    </Link>
                  ))}
                  </div>
                </div>
              )}
            </div>
            <NavLink to="/pricing" className="text-[color:var(--dt6-text)] hover:text-[color:var(--dt6-primary)]" data-testid="nav-pricing">Pricing</NavLink>
            <NavLink to="/partners" className="text-[color:var(--dt6-text)] hover:text-[color:var(--dt6-primary)]" data-testid="nav-partners">Partners</NavLink>
            <NavLink to="/compliance" className="text-[color:var(--dt6-text)] hover:text-[color:var(--dt6-primary)]" data-testid="nav-compliance">Compliance</NavLink>
            <NavLink to="/company" className="text-[color:var(--dt6-text)] hover:text-[color:var(--dt6-primary)]" data-testid="nav-company">Company</NavLink>
          </nav>
        </div>
        <div className="hidden md:flex items-center gap-3">
          {user && user.role ? (
            <>
              <Link to={portalHref} className="dt6-btn" data-testid="nav-portal">Portal</Link>
              <button className="dt6-btn" onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout">Log out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="dt6-btn" data-testid="nav-login">Log in</Link>
              <Link to="/audit" className="dt6-btn dt6-btn-primary" data-testid="nav-audit-cta">Free file audit</Link>
            </>
          )}
        </div>
        <button className="md:hidden dt6-btn" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t px-6 py-4 flex flex-col gap-3" style={{ borderColor: "var(--dt6-border)" }}>
          <Link to="/platform" onClick={() => setOpen(false)} data-testid="mnav-platform">Platform</Link>
          <Link to="/standard" onClick={() => setOpen(false)} data-testid="mnav-standard">The Standard</Link>
          {modules.map((m) => (
            <Link key={m.code} to={`/modules/${m.code}`} onClick={() => setOpen(false)} className="pl-3 text-sm text-[color:var(--dt6-muted)]" data-testid={`mnav-module-${m.code}`}>
              <span className="font-mono mr-2">{m.code}</span>{m.name}
            </Link>
          ))}
          <Link to="/pricing" onClick={() => setOpen(false)} data-testid="mnav-pricing">Pricing</Link>
          <Link to="/partners" onClick={() => setOpen(false)} data-testid="mnav-partners">Partners</Link>
          <Link to="/compliance" onClick={() => setOpen(false)} data-testid="mnav-compliance">Compliance</Link>
          <Link to="/company" onClick={() => setOpen(false)} data-testid="mnav-company">Company</Link>
          {user && user.role ? (
            <Link to={portalHref} onClick={() => setOpen(false)} className="dt6-btn dt6-btn-primary" data-testid="mnav-portal">Portal</Link>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="dt6-btn" data-testid="mnav-login">Log in</Link>
              <Link to="/audit" onClick={() => setOpen(false)} className="dt6-btn dt6-btn-primary" data-testid="mnav-audit-cta">Free file audit</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
